import React, { useState } from 'react'
import { useSubmitFinancialEligibilityHealthPlan } from '../../../../api/useSubmitPatientHistory'
import {
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledCheckbox,
  LabeledDate,
  LabeledNumber,
  LabeledText,
  MissingFields,
  todayIso,
  useRequiredFields,
} from '../../../fieldPrimitives'

// Not a real `PatientHistoryType`: a form-only choice submitted through
// `addFinancialEligibilityPatientHistory`. Dollars in the form, cents in the mutation.
const FinancialEligibilityHealthPlanFields = ({ uid }: { uid: string }) => {
  const [date, setDate] = useState(todayIso())
  const [description, setDescription] = useState('')
  const [intakeCostEstimate, setIntakeCostEstimate] = useState<number | ''>('')
  const [month1CostEstimate, setMonth1CostEstimate] = useState<number | ''>('')
  const [month2CostEstimate, setMonth2CostEstimate] = useState<number | ''>('')
  const [month3CostEstimate, setMonth3CostEstimate] = useState<number | ''>('')
  const [provideInsuranceDetails, setProvideInsuranceDetails] = useState(false)
  const [coverageActive, setCoverageActive] = useState(false)
  const [memberID, setMemberID] = useState('')
  const [groupNumber, setGroupNumber] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const { submit, submitting, error } = useSubmitFinancialEligibilityHealthPlan(uid)

  const required = useRequiredFields({
    'Date of event': Boolean(date),
    Description: Boolean(description.trim()),
    'Intake cost estimate ($)': intakeCostEstimate !== '',
    'Month 1 cost estimate ($)': month1CostEstimate !== '',
    'Month 2 cost estimate ($)': month2CostEstimate !== '',
    'Month 3 cost estimate ($)': month3CostEstimate !== '',
    ...(provideInsuranceDetails && {
      'Member ID': Boolean(memberID.trim()),
      'First name': Boolean(firstname.trim()),
      'Last name': Boolean(lastname.trim()),
    }),
  })

  const onSubmit = async () => {
    if (!required.attempt()) {
      return
    }

    await submit({
      date,
      description,
      costEstimate: {
        intakeCostEstimateInCents: Math.round(Number(intakeCostEstimate) * 100),
        month1CostEstimateInCents: Math.round(Number(month1CostEstimate) * 100),
        month2CostEstimateInCents: Math.round(Number(month2CostEstimate) * 100),
        month3CostEstimateInCents: Math.round(Number(month3CostEstimate) * 100),
      },
      insuranceSubscriber: provideInsuranceDetails
        ? { firstname, lastname, memberID, coverageActive, groupNumber, groupDescription, birthDate }
        : undefined,
    })
    setDescription('')
    required.reset()
  }

  return (
    <FieldSection>
      <FieldGrid>
        <LabeledDate
          label="Date of event"
          value={date}
          onChange={setDate}
          required
          error={required.error('Date of event')}
        />
      </FieldGrid>
      <LabeledText
        label="Description"
        value={description}
        onChange={setDescription}
        multiline
        required
        error={required.error('Description')}
      />

      <FieldSection title="Cost estimate">
        <FieldGrid>
          <LabeledNumber
            label="Intake cost estimate ($)"
            value={intakeCostEstimate}
            onChange={setIntakeCostEstimate}
            required
            error={required.error('Intake cost estimate ($)')}
          />
          <LabeledNumber
            label="Month 1 cost estimate ($)"
            value={month1CostEstimate}
            onChange={setMonth1CostEstimate}
            required
            error={required.error('Month 1 cost estimate ($)')}
          />
          <LabeledNumber
            label="Month 2 cost estimate ($)"
            value={month2CostEstimate}
            onChange={setMonth2CostEstimate}
            required
            error={required.error('Month 2 cost estimate ($)')}
          />
          <LabeledNumber
            label="Month 3 cost estimate ($)"
            value={month3CostEstimate}
            onChange={setMonth3CostEstimate}
            required
            error={required.error('Month 3 cost estimate ($)')}
          />
        </FieldGrid>
      </FieldSection>

      <LabeledCheckbox
        label="Provide insurance details"
        checked={provideInsuranceDetails}
        onChange={setProvideInsuranceDetails}
      />
      {provideInsuranceDetails && (
        <FieldSection title="Insurance subscriber">
          <LabeledCheckbox label="Coverage active" checked={coverageActive} onChange={setCoverageActive} />
          <FieldGrid>
            <LabeledText
              label="Member ID"
              value={memberID}
              onChange={setMemberID}
              required
              error={required.error('Member ID')}
            />
            <LabeledText label="Group number" value={groupNumber} onChange={setGroupNumber} />
            <LabeledText label="Group description" value={groupDescription} onChange={setGroupDescription} disabled />
            <LabeledText
              label="First name"
              value={firstname}
              onChange={setFirstname}
              required
              error={required.error('First name')}
            />
            <LabeledText
              label="Last name"
              value={lastname}
              onChange={setLastname}
              required
              error={required.error('Last name')}
            />
            <LabeledDate label="Birthdate" value={birthDate} onChange={setBirthDate} />
          </FieldGrid>
        </FieldSection>
      )}

      <MissingFields labels={required.missing} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
    </FieldSection>
  )
}

export default FinancialEligibilityHealthPlanFields
